// MCP connections bound to the person's Entra sign-in (poc CT-013, architecture 7.4 and 7.6; HPG MCP Authorisation
// Standard v2.0, rules MCP-20 "Entra check on use", MCP-22 "Configure and forget", MCP-07 lifetimes and MCP-10
// traceability). Every limit lives here, so the server (the checks, the lifetimes, the clean-up) and the pages (the
// consent page's lifetime line) agree.
//
// A connection is one token family: everything issued from one authorisation code. It holds the person's Entra
// sign-in (the MSAL token cache, encrypted) and asks Entra again on use, at most every ENTRA_CHECK_INTERVAL_MS.

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const DAY_MS = 24 * 60 * MINUTE_MS;

/** On use (a /mcp call or a refresh), Entra is asked again when it last vouched for the person longer ago than this. */
export const ENTRA_CHECK_INTERVAL_MS = 5 * MINUTE_MS;

/**
 * While Entra can't be reached (timeout, 5xx, 429), /mcp keeps serving for this long after the last confirmation,
 * logging each call as unconfirmed; then it answers 503 until Entra answers. A refresh is never refused for it.
 */
export const ENTRA_UNREACHABLE_GRACE_MS = 60 * MINUTE_MS;

/** Each call of the Entra check: the token renewal, GET /me and checkMemberGroups. */
export const ENTRA_CHECK_TIMEOUT_MS = 5 * SECOND_MS;

/** One check at a time per connection, across replicas: the lease a check takes. Others keep the last confirmation. */
export const ENTRA_CHECK_LEASE_MS = 30 * SECOND_MS;

/** The longest one check can take: its three calls (renewal, the account, the groups), and a little for the database. */
const ENTRA_CHECK_CALLS = 3;
const ENTRA_CHECK_SLACK_MS = SECOND_MS;
export const ENTRA_CHECK_MAX_MS = ENTRA_CHECK_CALLS * ENTRA_CHECK_TIMEOUT_MS + ENTRA_CHECK_SLACK_MS;

/**
 * A /mcp call whose last confirmation is past ENTRA_UNREACHABLE_GRACE_MS while another caller holds the lease waits
 * for that check's answer (at most ENTRA_CHECK_MAX_MS, re-reading the connection this often) instead of answering 503:
 * Entra isn't known to be down.
 */
export const ENTRA_CHECK_WAIT_MS = ENTRA_CHECK_MAX_MS;
export const ENTRA_CHECK_WAIT_POLL_MS = 300;

/** A refresh token expires this long after it's issued and rotates on every use: a connection unused this long ends. */
export const REFRESH_TOKEN_LIFETIME_DAYS = 45;
export const REFRESH_TOKEN_LIFETIME_MS = REFRESH_TOKEN_LIFETIME_DAYS * DAY_MS;

/** A connection ends this many calendar months after it was made (date-fns addMonths), used or not. */
export const CONNECTION_MAX_AGE_MONTHS = 12;

/** last_used_at is written at most this often per connection. */
export const LAST_USED_RESOLUTION_MS = 60 * SECOND_MS;

/** An ended connection's row is kept this long (as long as any refresh token of its family could exist), then deleted. */
export const ENDED_CONNECTION_RETENTION_MS = REFRESH_TOKEN_LIFETIME_MS;

/**
 * Why a connection ended (mcp_connections.end_reason): mirrors the lookup table mcp_connection_end_reasons, which the
 * column references as a foreign key since migration 0018 (S-2).
 */
export const MCP_CONNECTION_END_REASONS = [
  'person_disconnected',
  'admin_disconnected',
  'consent_withdrawn',
  'entra_refused',
  'account_disabled',
  'account_deleted',
  'sessions_revoked',
  'password_changed',
  'not_in_group',
  'idle',
  'max_age',
  'refresh_reuse',
  'code_reuse',
  'client_revoked',
  'made_before_ct013',
] as const;
export type McpConnectionEndReason = (typeof MCP_CONNECTION_END_REASONS)[number];

/** The reasons Entra's answer gives (the Entra check, or Graph for an Entra token presented directly at /mcp). */
export type EntraRefusalReason = Extract<
  McpConnectionEndReason,
  'entra_refused' | 'account_disabled' | 'account_deleted' | 'sessions_revoked' | 'password_changed' | 'not_in_group'
>;

/** The temporary error while Entra can't be reached past the grace (/mcp 503, the connect pages). */
export const ENTRA_UNREACHABLE_MESSAGE = 'We can’t confirm your access with Microsoft right now. Try again shortly.';

/** The consent page (S16): how long a connection lasts. */
export const MCP_CONNECTION_LIFETIME_TEXT =
  `This connection keeps working while you use it. It ends after ${REFRESH_TOKEN_LIFETIME_DAYS} days without use, ` +
  `${CONNECTION_MAX_AGE_MONTHS} months after you connect, or as soon as your HPG account or group membership no ` +
  'longer allows it; then you reconnect with one click.';
